import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  getMyEarnings,
  getMyEarningTransactions,
  formatDate,
  formatMoney,
  formatMonth,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// MY EARNINGS & PAYOUT  (Steps 16, 21, 22, 23, 24)
//
// The seven figures Step 16 lists — Current Balance, This Month, Previous
// Month, Total Earnings, Pending, Paid, History — plus the payout status
// (Step 24) and the 1st–8th window (Step 23).
//
// EVERY NUMBER ON THIS PAGE ARRIVES READY-MADE.
//   The page adds nothing up and converts nothing. The server aggregates the
//   earnings ledger and sends rupees; this file formats them. That is not
//   laziness — it is the point. A balance that the browser computed would be a
//   balance a browser could be made to compute differently.
//
// NOT ON THIS PAGE: any chart, graph, trend line or performance widget. This
// is a statement of account (Part B item 1).
// =====================================================

const STATUS_LABEL = {
  pending: "Pending",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  on_hold: "On Hold",
};

// The status of one ₹19 payment, said in terms of WHERE THE MONEY IS rather
// than in ledger vocabulary. "Earned" was being read as "already in my bank".
const EARNING_STATUS_LABEL = {
  earned: "In your balance",
  paid: "Sent to bank",
  refunded: "Refunded",
};

function ProEarnings() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getMyEarnings());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your earnings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTransactions = async () => {
    setShowTransactions((open) => !open);

    if (!transactions) {
      try {
        const res = await getMyEarningTransactions();
        setTransactions(res.items || []);
      } catch (err) {
        setTransactions([]);
      }
    }
  };

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading your earnings…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-container">
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { currentBalance, pending, paid, totalEarnings, thisMonth, previousMonth } = data;

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">💰 EARNINGS & PAYOUT</div>
          <h1 className="pro-hero-title">{formatMoney(currentBalance.rupees)}</h1>
          <p className="pro-hero-sub">Current balance · not yet paid out</p>
        </header>

        {/* ── STEP 19, said where it matters ── */}
        {!data.bank.payable && (
          <div className="pro-card pro-notice pro-notice-warn">
            <strong>
              {data.bank.added
                ? "Your bank email is not verified."
                : "You have not added your bank details."}
            </strong>
            <p>
              Only verified bank accounts receive payouts. Your earnings are safe and
              keep accruing — they are simply held until this is done.
            </p>
            <button
              type="button"
              className="pro-btn pro-btn-primary"
              onClick={() => navigate("/pro-bank")}
            >
              {data.bank.added ? "Verify email" : "Add bank details"}
            </button>
          </div>
        )}

        {/* ── THE SIX FIGURES  (Step 16) ── */}
        <section className="pro-money-grid" aria-label="Earnings summary">
          <div className="pro-money-card pro-money-primary">
            <small>Current Balance</small>
            <strong>{formatMoney(currentBalance.rupees)}</strong>
          </div>
          <div className="pro-money-card">
            <small>This Month</small>
            <strong>{formatMoney(thisMonth.netRupees)}</strong>
            <span className="pro-money-sub">{formatMonth(thisMonth.monthKey)}</span>
          </div>
          <div className="pro-money-card">
            <small>Previous Month</small>
            <strong>{formatMoney(previousMonth.netRupees)}</strong>
            <span className="pro-money-sub">{formatMonth(previousMonth.monthKey)}</span>
          </div>
          <div className="pro-money-card">
            <small>Total Earnings</small>
            <strong>{formatMoney(totalEarnings.rupees)}</strong>
          </div>
          <div className="pro-money-card">
            <small>Pending Payout</small>
            <strong>{formatMoney(pending.rupees)}</strong>
          </div>
          <div className="pro-money-card">
            <small>Paid Out</small>
            <strong>{formatMoney(paid.rupees)}</strong>
          </div>
        </section>

        {/* ── HOW THE SPLIT WORKS  (Step 21) ── */}
        <div className="pro-card pro-notice">
          <p>
            Every ₹19 unlock is split on the server:{" "}
            <strong>{data.commissionPercent}%</strong> to TheBlogsSphere and{" "}
            <strong>{100 - data.commissionPercent}%</strong> to you. Payouts for a
            completed month are made between the{" "}
            <strong>{data.payoutWindow.startDay}st and {data.payoutWindow.endDay}th</strong>{" "}
            of the following month.
          </p>
        </div>

        {/* ── PAYOUT HISTORY  (Step 16, Step 24) ── */}
        <section className="pro-content-section">
          <div className="pro-section-head">
            <h2>🏦 Payout History</h2>
            <button
              type="button"
              className="pro-btn pro-btn-ghost pro-btn-sm"
              onClick={() => navigate("/pro-bank")}
            >
              Bank details
            </button>
          </div>

          {/* Spelled out, because the two sections on this page are easy to
              confuse: "Payments received" is what readers PAID (earnings),
              "Payout History" is what actually LEFT for the bank. A creator who
              reads the first as money-in-my-account and then finds an empty
              bank account concludes the app is fake. */}
          <p className="pro-section-lead">
            💸 <strong>Money sent to your bank account.</strong> This is the only
            place where money actually leaves TheBlogsSphere and reaches you.
          </p>

          {data.history.length === 0 ? (
            <div className="pro-card pro-empty">
              <p>
                No payouts yet. A payout is raised after the month it covers has ended.
              </p>
            </div>
          ) : (
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Paid On</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((p) => (
                    <tr key={p._id}>
                      <td>{formatMonth(p.monthKey)}</td>
                      <td className="pro-table-strong">{formatMoney(p.amountRupees)}</td>
                      <td>
                        <span className={`pro-status-pill pro-status-${p.status}`}>
                          {STATUS_LABEL[p.status] || p.status}
                        </span>
                        {p.adminNote && (
                          <small className="pro-table-sub">{p.adminNote}</small>
                        )}
                      </td>
                      <td>{p.paidAt ? formatDate(p.paidAt) : "—"}</td>
                      <td>{p.reference || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── THE PAYMENTS BEHIND THE TOTALS ──
            So a creator can reconcile a figure instead of having to trust it. */}
        <section className="pro-content-section">
          <div className="pro-section-head">
            <h2>🧾 Payments received (earnings)</h2>
            <button
              type="button"
              className="pro-btn pro-btn-ghost pro-btn-sm"
              onClick={openTransactions}
            >
              {showTransactions ? "Hide" : "Show"}
            </button>
          </div>

          <p className="pro-section-lead">
            📥 <strong>What readers paid you — NOT money in your bank yet.</strong>{" "}
            Each ₹19 unlock is added to your balance here. It is transferred to
            your bank between the <strong>1st and 8th of the next month</strong>,
            and only then does it appear in Payout History above.
          </p>

          {showTransactions &&
            (transactions === null ? (
              <div className="pro-card pro-empty">
                <p>Loading…</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="pro-card pro-empty">
                <p>No payments yet.</p>
              </div>
            ) : (
              <div className="pro-table-scroll">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>Reader</th>
                      <th>Date</th>
                      <th>Paid</th>
                      <th>Commission</th>
                      <th>You earned</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t._id} className={t.status === "refunded" ? "pro-row-void" : ""}>
                        <td>{t.user?.name || "—"}</td>
                        <td>{formatDate(t.paidAt)}</td>
                        <td>{formatMoney(t.grossRupees)}</td>
                        <td>−{formatMoney(t.commissionRupees)}</td>
                        <td className="pro-table-strong">{formatMoney(t.netRupees)}</td>
                        <td>
                          {/* "Earned" was being read as "the money is mine
                              now". These labels say where the money actually
                              is, which is the question the creator is asking. */}
                          <span className={`pro-status-pill pro-status-${t.status}`}>
                            {EARNING_STATUS_LABEL[t.status] || t.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </section>

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/pro-home")}
        >
          ← Pro Home
        </button>
      </div>
    </div>
  );
}

export default ProEarnings;
